import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ia, inRn } from 'lib/helpers/utility';
import { useInvoice } from 'lib/hooks/queries/public/useInvoice';
import { bootPatientIntercom } from 'lib/helpers/intercomPatient';

import NotFound from 'components/NotFound';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import { getProceduresTotal } from 'components/practice/BeyondBilling/ElectronicInvoicing/components/Invoice/components/InvoiceFooter/InvoiceSummary/lib/helpers';

import SuccessPayment from './SuccessPayment';
import InvoiceWithTokenContent from './InvoiceWithTokenContent';
import InvoiceWithTokenSkeleton from './InvoiceWithTokenSkeleton';
import SecurePayment from './components/SecurePayment/SecurePayment';

const InvoiceWithToken = ({ invoice }) => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [transactionId, setTransactionId] = useState(null);

  // Patient support chat on the public tokenized invoice page only — not when
  // embedded with an invoice prop. Kiosk devices are blocked inside the helper.
  useEffect(() => {
    if (!invoice) bootPatientIntercom();
  }, [invoice]);

  const { data, isLoading } = useInvoice({
    params: { navigate, token },
    dependencies: [token],
    options: { enabled: !invoice && !!token }
  });

  const externalInvoice = invoice || data?.invoice;

  const type = externalInvoice?.type;
  const isKiosk = type === 'kiosk';
  const isAEOB = externalInvoice?.aeob;

  const practice = externalInvoice?.practice;
  const invoices = externalInvoice?.internal_invoices || [];
  const paid = externalInvoice?.paid;

  let patientInvoice = {
    discount: externalInvoice?.discount_amount_cents,
    tax: externalInvoice?.tax_percentage,
    tax_amount_cents: externalInvoice?.tax_amount_cents,
    surcharge_amount: inRn(externalInvoice?.surcharge_amount)
  };

  if (ia(invoices)) {
    const procedureTotalData = getProceduresTotal({ invoices, show: true, isAEOB });
    patientInvoice = { ...patientInvoice, ...procedureTotalData };
  }

  let { surcharge_amount = 0, total = 0 } = patientInvoice || {};

  if (isKiosk) total = externalInvoice?.total_amount_cents;

  const amountDue = total + (!isKiosk && !paid ? surcharge_amount : 0);

  if (!data?.invoice && !isLoading)
    return (
      <div className="h-dvh w-dvw overflow-hidden p-10">
        <NotFound />
      </div>
    );
  const onSuccess = (payload) => {
    const transactionId =
      typeof payload === 'string' || Number.isInteger(payload)
        ? payload
        : payload?.transactionId || payload?.receipt?.transaction?.id;
    setTransactionId(transactionId);
  };

  if (isLoading && !invoice) {
    return <InvoiceWithTokenSkeleton />;
  }

  if (transactionId) {
    return <SuccessPayment transactionId={transactionId} isKiosk={isKiosk} token={token} />;
  }

  return (
    <div className="h-dvh w-dvw overflow-hidden bg-[linear-gradient(180deg,#F0F9FF_0%,#B9E6FE_100%)] p-10">
      <div className="relative flex h-full flex-col rounded-xl bg-white shadow-[0px_0px_8px_rgba(65,70,81,0.1)]">
        <InvoiceWithTokenContent
          invoice={externalInvoice}
          invoices={invoices}
          amountDue={amountDue}
        />
        {amountDue > 0 && (
          <SecurePayment
            amountDue={amountDue}
            isKioskInvoice={isKiosk}
            practice={practice}
            patient={externalInvoice?.patient}
            invoices={invoices}
            token={token}
            onSuccess={onSuccess}
            invoice={externalInvoice}
          />
        )}
      </div>
    </div>
  );
};

export default withErrorBoundary(InvoiceWithToken);
